import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../../components/Button';
import { TAN_BG } from '../../data/constants';
import { useProducts } from '../../contexts/ProductContext';
import { useOrder } from '../../contexts/OrderContext';

const images = [
	{
		productId: 'br_s1400',
		image: '/images/available-bidets-home-page/brondell-s1400.jpg',
	},
  {
		productId: 'kh_e590',
		image: '/images/available-bidets-home-page/kohler-e590.webp',
	},
	{
		productId: 'om_gs',
		image: '/images/available-bidets-home-page/om-gs.jpg',
	},
  {
    productId: 'br_fsa-15',
    image: '/images/available-bidets-home-page/brondell-fsa_15.jpg',
  }
];

// Helper to extract product info
function getProductInfo(product: any) {
	const brand = product?.manufacturer || '';
	// Try both 'Model' and 'Model Name' (from products.json), fallback to 'Title'
	const model = product?.Model || product?.['Model Name'] || product?.Title || '';
	let price = '';
	if (product?.variations && product.variations.length > 0) {
		const defaultVar = product.variations.find((v: any) => v.isDefaultVariation) || product.variations[0];
		price = defaultVar?.resellerPrice ? `$${defaultVar.resellerPrice}` : '';
	}
	return { brand, model, price };
}

export function AvailableBidets() {
	const navigate = useNavigate();
	const [current, setCurrent] = useState(0);
	const [fade, setFade] = useState(true);
	const { openBidetModal } = useOrder();
	const { products: bidets } = useProducts();

	// Auto-cycle every 5 seconds
	useEffect(() => {
		const interval = setInterval(() => {
			setFade(false);
			setTimeout(() => {
				setCurrent((prev) => (prev + 1) % images.length);
				setFade(true);
			}, 300); // fade out duration
		}, 5000);
		return () => clearInterval(interval);
	}, []);

	// Manual navigation
	const goTo = (idx: number) => {
		setFade(false);
		setTimeout(() => {
			setCurrent(idx);
			setFade(true);
		}, 300);
	};
	const prev = () => goTo((current - 1 + images.length) % images.length);
	const next = () => goTo((current + 1) % images.length);

	// Get product info for current image
	const currentImage = images[current];
	const product = bidets.find((b: any) => b.id === currentImage.productId);
	const { brand, model, price } = getProductInfo(product);

	return (
		<section className="pb-8 pt-4" style={{ backgroundColor: TAN_BG }}>
			<div className="container mx-auto px-4">
				<div className="flex flex-col md:flex-row md:items-stretch md:justify-center items-center justify-center max-w-[1200px] mx-auto h-auto md:h-[500px]">
					<div className="bg-white w-full md:w-1/2 flex flex-col justify-center items-center
            rounded-b-xl md:rounded-l-xl md:rounded-bl-xl md:rounded-r-none md:rounded-b-none
            shadow-lg p-8 md:flex-1 order-2 md:order-1 h-full"
					>
						<div className="flex flex-col items-center text-center max-w-[400px] w-full mx-auto">
							<h2 className="text-xl font-bold mb-2">Available Bidets</h2>
							<p className="mb-4 text-sm opacity-90">
								Explore our curated selection of bidets, handpicked for New York homes. Whether you want something simple or feature-packed, we have the perfect fit for your bathroom and budget.
							</p>
							<Button
								type="button"
								cta
								onClick={() => navigate('/bidets')}
								className="mt-2"
							>
								See all bidet options
							</Button>
						</div>
					</div>
					<div className="w-full md:w-1/2 flex-shrink-0 flex-grow-0 order-1 md:order-2 flex items-stretch justify-stretch h-[300px] md:h-full relative">
						<button
							aria-label="Previous image"
							onClick={prev}
							className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white rounded-full p-2 shadow focus:outline-none"
							style={{ display: 'block' }}
						>
							<span className="sr-only">Previous</span>
							<svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 19l-7-7 7-7" /></svg>
						</button>
						<button
							aria-label="Next image"
							onClick={next}
							className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white rounded-full p-2 shadow focus:outline-none"
							style={{ display: 'block' }}
						>
							<span className="sr-only">Next</span>
							<svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg>
						</button>
						<img
							src={currentImage.image}
							alt={`Bidet product ${current + 1}`}
							className={`w-full h-full object-cover transition-opacity duration-300 rounded-t-xl md:rounded-tr-xl md:rounded-br-xl md:rounded-l-none md:rounded-t-none ${fade ? 'opacity-100' : 'opacity-0'}`}
							style={{ maxHeight: '500px' }}
							draggable="false"
						/>
						{/* Floating info box */}
						{product && (
							<button
								className="absolute bottom-4 right-4 bg-white/95 rounded shadow-lg px-4 py-2 text-left text-xs md:text-sm cursor-pointer border border-gray-200 hover:shadow-xl transition-all"
								style={{ minWidth: 120, zIndex: 15 }}
								onClick={() => openBidetModal(currentImage.productId)}
							>
								<div className="font-semibold text-gray-800">{brand}</div>
								<div className="text-gray-600">{model}</div>
								<div className="font-bold text-primary mt-1">{price}</div>
								<div className="text-[10px] text-gray-500 underline mt-1">View details</div>
							</button>
						)}
					</div>
				</div>
			</div>
		</section>
	);
}
